import { Intent } from "@blueprintjs/core";
import {
  FeatureGate,
  FeatureGateClickedSource,
  FeatureGateCta,
  FeatureGateTier,
  FeatureGateWithAddons,
  LABELS_BY_FEATURE_GATE_TIER,
  OrgRole,
  SalesUpsellFeatureGates,
} from "@hex/common";
import { rgba } from "polished";
import React, { ReactNode, useCallback } from "react";
import styled, { css } from "styled-components";

import {
  HexAnchorButton,
  HexLinkButton,
  HexNonIdealStateProps,
  Txt,
} from "../../hex-components";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { useDispatch } from "../../redux/hooks";
import { setContactSalesFormOpen } from "../../redux/slices/uiSlice";
import { Routes } from "../../route/routes";
import Diamond from "../../static/illustration/illustration-diamond.inline.svg";
import MiniDiamond from "../../static/illustration/illustration-mini-diamond.inline.svg";
import { trackFeatureGateClicked } from "../../util/featureGateUtils";
import { ContactAnAdmin } from "../common/ContactAnAdmin";
import { PerlinDotGrid } from "../common/PerlinBackground";
import { Heading } from "../Heading";

export function getUpgradeTierLabel(
  featureGateUpgradeToTier:
    | Record<FeatureGate, FeatureGateTier | null>
    | undefined,
  featureGate: FeatureGateWithAddons,
): string | null {
  const tierList = featureGateUpgradeToTier;
  if (!tierList || !(featureGate in tierList)) {
    return null;
  }

  return LABELS_BY_FEATURE_GATE_TIER[
    // We can validate this because we've checked `in tierList` above
    tierList[featureGate as FeatureGate] as FeatureGateTier
  ];
}

export const ContactSalesAction = React.memo(function ContactSalesAction({
  buttonProps,
  featureGate,
  onClick,
}: {
  buttonProps: React.ComponentProps<typeof HexAnchorButton>;
  onClick: React.ComponentProps<typeof HexAnchorButton>["onClick"];
  featureGate: SalesUpsellFeatureGates;
}) {
  const dispatch = useDispatch();

  const clickButton = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      dispatch(setContactSalesFormOpen(featureGate));
      onClick?.(e);
    },
    [onClick, dispatch, featureGate],
  );

  return (
    <HexAnchorButton
      intent={Intent.PRIMARY}
      text="Contact sales"
      onClick={clickButton}
      {...buttonProps}
    />
  );
});

interface FeatureGateIconProps {
  icon?: ReactNode;
  $isTooltip?: boolean;
}
export const FeatureGateIcon: React.ComponentType<FeatureGateIconProps> =
  React.memo(function FeatureGateIcon({
    $isTooltip,
    icon,
  }: FeatureGateIconProps) {
    return (
      <FeatureGateIconWrapper $isTooltip={$isTooltip}>
        {icon || ($isTooltip ? <MiniDiamond /> : <Diamond />)}
      </FeatureGateIconWrapper>
    );
  });

export type FeatureGateAndSalesContactProps =
  | {
      /**
       * Name of feature gated feature used for event tracking
       */
      featureGate: FeatureGateWithAddons;

      /**
       * Whether the feature gate requires a sales contact to be reached out to enable (i.e. enterprise features)
       */
      requiresSalesContact?: false;
    }
  | {
      featureGate: SalesUpsellFeatureGates;
      requiresSalesContact: true;
    };

export type FeatureGateCTAProps = FeatureGateAndSalesContactProps & {
  className?: string;
  isTooltip?: boolean;
};

const FeatureGateCTA: React.ComponentType<FeatureGateCTAProps> = React.memo(
  function FeatureGateCTA({
    className,
    featureGate,
    isTooltip,
    requiresSalesContact,
  }: FeatureGateCTAProps) {
    const currentUser = useCurrentUser();
    const isAdmin = currentUser?.orgRole === OrgRole.ADMIN;

    const onClick = useCallback(() => {
      trackFeatureGateClicked({
        feature: featureGate,
        source: FeatureGateClickedSource.FEATURE_DISABLED,
        orgRole: currentUser?.orgRole,
        cta: requiresSalesContact
          ? FeatureGateCta.CONTACT_SALES
          : FeatureGateCta.VIEW_PLANS,
      });
    }, [currentUser?.orgRole, featureGate, requiresSalesContact]);

    return (
      <div className={className}>
        {isAdmin && (
          <div
            css={`
              a {
                text-decoration: none;
              }
            `}
          >
            {requiresSalesContact ? (
              <ContactSalesAction
                buttonProps={{
                  large: !isTooltip,
                }}
                featureGate={featureGate}
                onClick={onClick}
              />
            ) : (
              <HexLinkButton
                intent={Intent.PRIMARY}
                large={!isTooltip}
                target="_blank"
                text="View plans"
                to={Routes.SETTINGS.getUrl({
                  subView: "tiers",
                })}
                onClick={onClick}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);

export type FeatureGateZeroStateProps = Omit<HexNonIdealStateProps, "action"> &
  FeatureGateCTAProps & {
    $noShadow?: boolean;
    titleOverride?: React.ReactNode;
    isTooltip?: boolean;
    icon?: React.ReactNode;
    illustration?: React.ReactNode;
  } & (
    | {
        action: string;
        descriptionOverride?: undefined;
      }
    | {
        action?: undefined;
        descriptionOverride: string;
      }
  );

export const FeatureGateZeroState: React.ComponentType<FeatureGateZeroStateProps> =
  React.memo(function FeatureGateZeroState({
    $noShadow = false,
    action: action_,
    descriptionOverride,
    featureGate,
    icon,
    illustration,
    isTooltip = false,
    requiresSalesContact,
    titleOverride,
  }: FeatureGateZeroStateProps) {
    const currentUser = useCurrentUser();
    const upgradeToTierLabel = getUpgradeTierLabel(
      currentUser?.org.featureGateUpgradeToTier,
      featureGate,
    );

    const title = isTooltip ? (
      <Txt
        css={`
          margin-bottom: 4px;
        `}
        fontSize="large"
        fontWeight="medium"
      >
        {titleOverride ?? "Time to upgrade?"}
      </Txt>
    ) : (
      <Heading css="margin-bottom: 8px;" renderAs="h3">
        {titleOverride ?? "Time to upgrade?"}
      </Heading>
    );

    // We add a trailing period, so remove it if original prop had one
    const action = action_?.replace(/\.$/, "");
    const desc = (
      <Txt fontColor="muted" fontSize={isTooltip ? "default" : "large"}>
        {descriptionOverride ??
          (upgradeToTierLabel
            ? `Upgrade to the ${upgradeToTierLabel} plan to ${action}.`
            : `Upgrade your plan to ${action}.`)}
      </Txt>
    );

    const isAdmin = currentUser?.orgRole === OrgRole.ADMIN;
    return (
      <ZeroStateWrapper>
        <ZeroState $isTooltip={isTooltip} $noShadow={$noShadow}>
          {isTooltip && illustration && (
            <TooltipIllustrationWrapper>
              {illustration}
            </TooltipIllustrationWrapper>
          )}

          <ContentWrapper $isTooltip={isTooltip}>
            {(!isTooltip || icon) && (
              <FeatureGateIcon $isTooltip={isTooltip} icon={icon} />
            )}
            <Row>
              {isTooltip && !icon && !illustration && (
                <MiniDiamondIconWrapper>
                  <MiniDiamond />
                </MiniDiamondIconWrapper>
              )}
              {title}
            </Row>
            {isAdmin ? (
              <>{desc}</>
            ) : (
              <>
                {desc}
                <div
                  css={`
                    margin-top: 12px;
                  `}
                >
                  <ContactAnAdmin button={!isTooltip ? "large" : "default"} />
                </div>
              </>
            )}
            <FeatureGateCTA
              css="margin-top: 12px;"
              isTooltip={isTooltip}
              {...(requiresSalesContact
                ? {
                    requiresSalesContact: true,
                    featureGate: featureGate,
                  }
                : {
                    featureGate: featureGate,
                  })}
            />
          </ContentWrapper>
          {!isTooltip && illustration && (
            <FullSizeIllustrationWrapper>
              {illustration}
            </FullSizeIllustrationWrapper>
          )}
        </ZeroState>
        <PerlinDotGrid />
      </ZeroStateWrapper>
    );
  });

export const ZeroState = styled.div<{
  $noShadow?: boolean;
  $isTooltip?: boolean;
}>`
  /* Ensure it's higher than the perlin dot grid */
  z-index: 1;
  padding: 30px;
  border-radius: ${({ theme }) => theme.borderRadius};
  align-items: flex-start;
  overflow: hidden;

  box-shadow: ${({ $noShadow, theme }) =>
    $noShadow ? "none" : theme.boxShadow.CARD};

  background: linear-gradient(
    to left,
    ${({ theme }) => rgba(theme.backgroundColor.DEFAULT, 0.1)} 0%,
    ${({ theme }) => rgba(theme.backgroundColor.DEFAULT, 0.5)} 100%
  );

  ${({ $isTooltip }) =>
    $isTooltip &&
    css`
      padding: 0;
      width: 350px;
    `}
  ${({ $isTooltip }) =>
    !$isTooltip &&
    css`
      display: flex;
      flex-direction: row;
    `}
`;

const ColorIconWrapper = styled.div`
  svg path {
    color: ${({ theme }) => theme.highlightColor};
  }
`;

const MiniDiamondIconWrapper = styled(ColorIconWrapper)`
  margin-bottom: 2px;
`;

const FeatureGateIconWrapper = styled(ColorIconWrapper)<{
  $isTooltip?: boolean;
}>`
  padding-bottom: 12px;
  ${({ $isTooltip }) =>
    $isTooltip &&
    css`
      padding: 16px;
    `}
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ZeroStateWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ContentWrapper = styled.div<{ $isTooltip?: boolean }>`
  max-width: 400px;
  display: flex;
  flex-direction: column;

  ${({ $isTooltip }) =>
    $isTooltip &&
    css`
      padding: 16px;
    `}
`;

const TooltipIllustrationWrapper = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  max-height: 200px;
  overflow: hidden;
  padding: 5px;
  background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
`;

const FullSizeIllustrationWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  height: 200px;
  svg {
    height: auto;
    max-width: 600px;
    width: 100%;
    margin: 0 auto;
  }
`;
